import React, { useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, CollapseWithIcon, EnumIconButtonShape, IconButton, Typography } from 'shared/ui'
import { TextField } from 'shared/ui/TextField'
import { useCopyToClipboard } from 'shared/hooks/useCopyToClipboard'
import claude1 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/claude/claude1.png'
import claude2 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/claude/claude2.png'
import claude3 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/claude/claude3.png'
import claude4 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/claude/claude4.png'
import claude5 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/claude/claude5.png'
import openai1 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/openai/openai1.png'
import openai2 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/openai/openai2.png'
import openai3 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/openai/openai3.png'
import openai4 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/openai/openai4.png'
import openai5 from 'shared/assets/images/integrationsSliders/salesmsg_mcp/openai/openai5.png'
import { Attachment } from 'entities/Attachment/model/Attachment'
import type { Integration } from 'entities/Integrations'
import { usePresentationModeContext } from 'widgets/PresentationMode'
import sharedStyles from 'pages/settings/pages/integrations/pages/integrationSettings/ui/styles.module.scss'
import styles from './styles.module.scss'

const MCP_SERVER_URL = 'https://mcp.salesmessage.com'

const CLAUDE_IMAGE_URLS = [claude2, claude3, claude4, claude5, claude1]
const OPENAI_IMAGE_URLS = [openai1, openai2, openai3, openai4, openai5]

const CLAUDE_STEPS = [
  'Open Settings',
  'Go to Connectors',
  'Click the plus button, then select Add custom connector',
  'Paste your MCP URL and click Add',
]

const OPENAI_STEPS = [
  'Open Settings',
  'Go to Apps',
  'Enable Developer mode',
  'Click Create app',
  'Paste your MCP URL and click Create',
]

type Props = {
  integration: Integration
}

export const IntegrationMCPSettings = observer(({ integration: _ }: Props) => {
  const [openSection, setOpenSection] = useState<'claude' | 'openai' | null>(null)
  const { onCopy, copied } = useCopyToClipboard(MCP_SERVER_URL, {
    showToast: true,
    toastTitle: 'Copied',
    toastType: 'info',
  })
  const presentationStore = usePresentationModeContext()

  const claudeAttachments = useMemo(
    () => CLAUDE_IMAGE_URLS.map((url, i) => new Attachment({ imageUrl: url, id: i })),
    []
  )

  const openaiAttachments = useMemo(
    () => OPENAI_IMAGE_URLS.map((url, i) => new Attachment({ imageUrl: url, id: `openai_${i}` })),
    []
  )

  return (
    <div className={styles.root}>
      <div className={styles.urlRow}>
        <TextField
          className={styles.urlInput}
          size='medium'
          variant='stroke'
          value={MCP_SERVER_URL}
          readOnly
        />
        <Button
          onClick={onCopy}
          size='medium'
          text={copied ? 'Copied' : 'Copy URL'}
          icon={'copy'}
          iconProps={{ padding: '4px 0' }}
        />
      </div>
      <CollapseWithIcon
        opened={openSection === 'claude'}
        onClick={() => setOpenSection((s) => (s === 'claude' ? null : 'claude'))}
        icon='claude'
        title='Connect to Claude'
        label='Add as a custom connector in Settings'
        headerClassName={sharedStyles.collapseWithIcon}
        contentClassName={styles.collapseContent}
      >
        <>
          <div className={styles.imageRow}>
            {CLAUDE_IMAGE_URLS.map((src, i) => (
              <button
                key={i}
                className={styles.thumbnail}
                onClick={() => presentationStore.setAttachments(claudeAttachments, i)}
              >
                <img src={src} alt={`Claude step ${i + 1}`} />
                <div className={styles.thumbnailOverlay}>
                  <IconButton
                    icon='solidExpand2'
                    fontSize={16}
                    color={'darkPrimary'}
                    ariaLabel='ShowPresentationMode'
                    shape={EnumIconButtonShape.Rounded}
                  />
                </div>
              </button>
            ))}
          </div>
          <Typography
            tag='div'
            variant='body-md-medium'
            ariaLabel='IntegrationMCPSettings_HowToConnect_title'
            marginBottom={4}
          >
            How to connect
          </Typography>
          <ul className={styles.stepsList}>
            {CLAUDE_STEPS.map((step, index) => (
              <li key={step}>
                <Typography
                  variant='body-md-regular'
                  ariaLabel={`IntegrationMCPSettings_HowToConnect_${index}`}
                  color='tertiary'
                >
                  {step}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      </CollapseWithIcon>
      <CollapseWithIcon
        opened={openSection === 'openai'}
        onClick={() => setOpenSection((s) => (s === 'openai' ? null : 'openai'))}
        icon='openAI'
        title='Connect to OpenAI'
        label='Add as a connector in ChatGPT Settings'
        headerClassName={sharedStyles.collapseWithIcon}
        contentClassName={styles.collapseContent}
      >
        <>
          <div className={styles.imageRow}>
            {OPENAI_IMAGE_URLS.map((src, i) => (
              <button
                key={i}
                className={styles.thumbnail}
                onClick={() => presentationStore.setAttachments(openaiAttachments, i)}
              >
                <img src={src} alt={`OpenAI step ${i + 1}`} />
                <div className={styles.thumbnailOverlay}>
                  <IconButton
                    icon='solidExpand2'
                    fontSize={16}
                    color={'darkPrimary'}
                    ariaLabel='ShowPresentationMode'
                    shape={EnumIconButtonShape.Rounded}
                  />
                </div>
              </button>
            ))}
          </div>
          <Typography
            tag='div'
            variant='body-md-medium'
            ariaLabel='IntegrationMCPSettings_OpenAI_HowToConnect_title'
            marginBottom={4}
          >
            How to connect
          </Typography>
          <ul className={styles.stepsList}>
            {OPENAI_STEPS.map((step, index) => (
              <li key={step}>
                <Typography
                  variant='body-md-regular'
                  ariaLabel={`IntegrationMCPSettings_OpenAI_HowToConnect_${index}`}
                  color='tertiary'
                >
                  {step}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      </CollapseWithIcon>
    </div>
  )
})
